import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { TERRAZZO_CATALOGUE, FINISHES } from "@/lib/data";

const PRODUCTS_HERO = "https://www.parmandesigns.com/assets/img/terrazzo.jpg";

export default function Products() {
  const [lightbox, setLightbox] = useState(null); // {image, code, series} | null

  const close = useCallback(() => setLightbox(null), []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close]);

  return (
    <div data-testid="products-page" className="bg-cream">
      {/* ===== Page Hero ===== */}
      <section className="px-4 sm:px-6 md:px-16 lg:px-20 pt-20 sm:pt-24 md:pt-32 pb-12 sm:pb-16">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid grid-cols-12 gap-6 sm:gap-8">
            <div className="col-span-12 md:col-span-4">
              <p className="font-mono-label text-stone-text">Catalogue</p>
            </div>
            <div className="col-span-12 md:col-span-8">
              <h1 className="font-serif-display text-4xl sm:text-5xl md:text-7xl leading-[1.0]">
                The terrazzo <em className="italic text-terracotta">collection</em>.
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Intro split ===== */}
      <section className="px-4 sm:px-6 md:px-16 lg:px-20 pb-12 sm:pb-16 md:pb-20">
        <div className="max-w-[1600px] mx-auto grid grid-cols-12 gap-8 sm:gap-10 md:gap-12 lg:items-center">
          <div className="col-span-12 lg:col-span-7 space-y-4 sm:space-y-5 order-2 lg:order-1">
            <p className="font-serif-display text-xl sm:text-2xl md:text-3xl leading-snug">
              Small aggregates, large aggregates, extra-large aggregates?
            </p>
            <p className="text-sm sm:text-base text-stone-text leading-relaxed">
              A common question in a terrazzo meeting — and one we love answering. Everything is customised from aggregate size to colour combination. We don't want you to repeat designs; we offer full freedom to draw the concept, because our dream project is yet to be designed.
            </p>
            <p className="text-sm sm:text-base text-stone-text leading-relaxed">
              With deep experience in <b className="text-ink">Precast Terrazzo</b> and <b className="text-ink">Cast-on-site Terrazzo</b>, brass and marble inlays, we work on tiles, slabs, and poured floors. The catalogue below is a starting point — a library of compositions we have run before.
            </p>
            <p className="text-sm sm:text-base text-stone-text leading-relaxed">
              Click any sample to view it large. Reference the code (e.g. <b className="text-ink">TI-09</b>) when requesting a sample and we'll cast it to your size and finish.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-5 order-1 lg:order-2">
            <div className="image-zoom-wrap aspect-[4/5] max-w-md mx-auto lg:max-w-none">
              <img src={PRODUCTS_HERO} alt="Terrazzo" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* ===== Single catalogue grid ===== */}
      <section
        id="catalogue"
        data-testid="catalogue-section"
        className="scroll-mt-20 sm:scroll-mt-24 px-4 sm:px-6 md:px-16 lg:px-20 py-10 sm:py-14 md:py-18 bg-stone"
      >
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center mb-8 sm:mb-10 md:mb-14">
            <p className="font-mono-label text-terracotta mb-3">— The catalogue</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl md:text-6xl">
              {TERRAZZO_CATALOGUE.length} Terrazzo <em className="italic text-terracotta">compositions</em>
            </h2>
            <div className="w-10 sm:w-12 h-px bg-ink/30 mx-auto mt-5 sm:mt-6" />
            <p className="font-mono-label text-stone-text mt-5 sm:mt-6 text-[0.65rem] sm:text-xs">
              Every code (TI-01 → TI-{String(TERRAZZO_CATALOGUE.length).padStart(2,"0")}) can be cast to your size, thickness & finish.
            </p>
          </div>

          <div
            data-testid="catalogue-grid"
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5 md:gap-7"
          >
            {TERRAZZO_CATALOGUE.map((item, i) => (
              <button
                key={item.code}
                data-testid={`tile-${i + 1}`}
                onClick={() => setLightbox(item)}
                className="group text-left focus:outline-none focus:ring-2 focus:ring-terracotta focus:ring-offset-2 focus:ring-offset-cream"
                aria-label={`View ${item.code}`}
              >
                <div className="image-zoom-wrap aspect-square bg-warm/40 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.code}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="mt-3 sm:mt-4 flex items-baseline justify-between gap-2 sm:gap-3 border-t border-warm pt-2.5 sm:pt-3">
                  <p className="font-serif-display text-base sm:text-xl md:text-2xl group-hover:text-terracotta transition-colors leading-none">
                    {item.code}
                  </p>
                  <p className="font-mono-label text-stone-text text-[0.55rem] sm:text-xs">Terrazzo India</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Finishes Available ===== */}
      <section data-testid="finishes-section" className="px-4 sm:px-6 md:px-16 lg:px-20 py-12 sm:py-16 md:py-20 bg-ink text-cream">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center mb-8 sm:mb-10 md:mb-14">
            <p className="font-mono-label text-cream/60 mb-3">Surface</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl md:text-6xl">
              Finishes <em className="italic" style={{ color: "#c8ac7b" }}>available</em>.
            </h2>
            <div className="w-10 sm:w-12 h-px bg-cream/30 mx-auto mt-5 sm:mt-6" />
          </div>

          <div className="space-y-14 sm:space-y-20">
            {FINISHES.map((f) => (
              <div
                key={f.id}
                data-testid={`finish-${f.id}`}
                className="grid grid-cols-12 gap-6 sm:gap-8 md:gap-12 items-start"
              >
                <div className="col-span-12 md:col-span-6">
                  <p className="font-mono-label text-cream/60 mb-3 sm:mb-4">{f.name}</p>
                  <div className="image-zoom-wrap aspect-[4/3]">
                    <img src={f.image} alt={f.name} className="w-full h-full object-cover" />
                  </div>
                </div>
                <div className="col-span-12 md:col-span-6 md:pt-2">
                  <h3 className="font-serif-display text-2xl sm:text-3xl md:text-4xl mb-5 sm:mb-6">{f.name}</h3>
                  <p className="font-mono-label text-cream/60 mb-4 sm:mb-5">Uses</p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-1 sm:gap-y-2 gap-x-6 sm:gap-x-8 border-t border-cream/15 pt-4 sm:pt-5">
                    {f.uses.map(([k, v]) => (
                      <li key={k} className="flex items-baseline justify-between gap-3 sm:gap-4 border-b border-cream/10 py-1.5 sm:py-2 text-xs sm:text-sm">
                        <span className="text-cream/75">{k}</span>
                        <span
                          className={`font-mono-label ${
                            v === "Yes" ? "text-terracotta" : "text-cream/40"
                          }`}
                        >
                          {v}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="px-4 sm:px-6 md:px-16 lg:px-20 py-12 sm:py-16 md:py-20 bg-cream">
        <div className="max-w-[1200px] mx-auto text-center">
          <p className="font-mono-label text-stone-text mb-3 sm:mb-4">Next step</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl md:text-6xl leading-[1.05]">
            See a code you like? <br />
            <em className="italic text-terracotta">Request a sample</em>.
          </h2>
          <p className="mt-5 sm:mt-6 text-sm sm:text-base text-stone-text max-w-xl mx-auto">
            Quote the TD code on the enquiry form, mention your size and finish, and a senior from the studio will ship a sample within days.
          </p>
          <Link
            to="/request-quote"
            data-testid="products-cta"
            className="inline-flex mt-8 sm:mt-10 font-mono-label px-7 sm:px-8 py-3.5 sm:py-4 bg-ink text-cream hover:bg-terracotta transition-colors"
          >
            Request a Sample →
          </Link>
        </div>
      </section>

      {/* ===== Lightbox ===== */}
      {lightbox && (
        <div
          data-testid="lightbox"
          onClick={close}
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[100] bg-ink/95 backdrop-blur flex items-center justify-center p-4 sm:p-6 md:p-12 cursor-zoom-out"
        >
          <button
            data-testid="lightbox-close"
            onClick={close}
            aria-label="Close"
            className="absolute top-4 right-4 sm:top-6 sm:right-6 md:top-10 md:right-10 font-mono-label text-cream hover:text-terracotta z-10 px-3 py-2 bg-ink/50 rounded"
          >
            Close ✕
          </button>
          <div className="max-w-[92vw] max-h-[85vh] flex flex-col items-center gap-4 sm:gap-5" onClick={(e)=>e.stopPropagation()}>
            <img
              src={lightbox.image}
              alt={lightbox.code}
              className="max-w-full max-h-[72vh] sm:max-h-[78vh] object-contain shadow-2xl"
            />
            <div className="flex items-baseline gap-4 sm:gap-6 text-cream">
              <p className="font-serif-display text-2xl sm:text-3xl md:text-4xl">{lightbox.code}</p>
              <p className="font-mono-label text-cream/60">Terrazzo India</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
